import { useNavigate } from "react-router-dom";
import { VenereBox, VenereButton, VenereProgressBar } from "../../shared/ui";
import { useWeightChart } from "../hooks/useWeightChart";
import { useWeightData } from "../hooks/useWeightData";

const VenereProgressBox = () => {
    const { goalWeights, actualWeights } = useWeightData();

    const {
        filteredActualWeights,
        filteredGoalWeights,
        chartHeight,
        chartWidth,
        weightToY,
        weekToX,
        getYAxisLabels,
        goalPath,
        actualPath,
        tooltip,
        handlePointHover,
        handlePointLeave,
        responsiveXLabelPadding
    } = useWeightChart(actualWeights, goalWeights);

    const navigate = useNavigate()

    return (
        <div className="flex flex-col gap-4">
            <VenereBox variant="lavender" className="max-w-[800px]">
                <div className="h-full w-full flex flex-col gap-4">
                    <div className="w-full flex flex-col md:flex-row gap-4 md:justify-between md:items-center">
                        <div className="venere-text-primary">
                            Progressi Peso
                        </div>

                        <div className="flex justify-start">
                            <VenereButton variant="action">
                                Visualizza i tuoi parametri
                            </VenereButton>
                        </div>
                    </div>

                    {/* Chart Container */}
                    <div className="flex-1 min-h-[250px] bg-white rounded-lg p-4">
                        {/* Y-axis title */}
                        <div className="venere-text-secondary">
                            Peso (kg)
                        </div>

                        {/* Chart area with horizontal scroll */}
                        <div className="flex-1 flex overflow-x-auto">
                            {/* Y-axis labels */}
                            <div className="flex flex-col justify-between text-xs text-gray-500 mr-4 mb-8">
                                {getYAxisLabels().map((weight, i) => (
                                    <div key={`y-label-${i}`} className="h-6 flex items-center">
                                        {weight.toFixed(0)}
                                    </div>
                                ))}
                            </div>

                            {/* Graph + X-axis container */}
                            <div className="flex-1 flex flex-col">
                                {/* Graph */}
                                <div className="flex-1 relative">
                                    <svg
                                        width={chartWidth}
                                        height={chartHeight}
                                        className="w-full h-full"
                                        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                                        onClick={handlePointLeave}
                                    >
                                        {/* Goal line (rendered first, behind) */}
                                        <path
                                            d={goalPath}
                                            stroke="var(--venere-purple)"
                                            strokeWidth="1.5"
                                            fill="none"
                                        />

                                        {/* Actual line (rendered second, on top) */}
                                        <path
                                            d={actualPath}
                                            stroke="var(--venere-royal)"
                                            strokeWidth="1.5"
                                            fill="none"
                                        />

                                        {/* Data points for goal weights (rendered third, behind actual points) */}
                                        {filteredGoalWeights.map((weight, index) => {
                                            const x = weekToX(index, filteredGoalWeights.length);
                                            const y = weightToY(weight);
                                            return (
                                                <circle
                                                    key={`point-goal-${index}`}
                                                    cx={x}
                                                    cy={y}
                                                    r="4"
                                                    fill="var(--venere-purple)"
                                                    onMouseEnter={(e) => handlePointHover(e, 'goal', weight, index + 1)}
                                                    onMouseLeave={handlePointLeave}
                                                    onClick={(e) => handlePointHover(e, 'goal', weight, index + 1)}
                                                />
                                            );
                                        })}

                                        {/* Data points for actual weights (rendered last, on top) */}
                                        {filteredActualWeights.map((weight, index) => {
                                            const x = weekToX(index, filteredActualWeights.length);
                                            const y = weightToY(weight);
                                            return (
                                                <circle
                                                    key={`point-actual-${index}`}
                                                    cx={x}
                                                    cy={y}
                                                    r="4"
                                                    fill="var(--venere-royal)"
                                                    onMouseEnter={(e) => handlePointHover(e, 'real', weight, index + 1)}
                                                    onMouseLeave={handlePointLeave}
                                                    onClick={(e) => handlePointHover(e, 'real', weight, index + 1)}
                                                />
                                            );
                                        })}

                                        {/* Vertical alignment lines */}
                                        {Array.from({ length: filteredActualWeights.length }, (_, i) => i).map((index) => {
                                            const x = weekToX(index, filteredActualWeights.length);
                                            return (
                                                <line
                                                    key={`alignment-line-${index}`}
                                                    x1={x}
                                                    y1={0}
                                                    x2={x}
                                                    y2={chartHeight}
                                                    stroke="rgba(0,0,0,0.1)"
                                                    strokeWidth="1"
                                                    strokeDasharray="2,2"
                                                />
                                            );
                                        })}
                                    </svg>

                                    {/* Tooltip */}
                                    {tooltip.show && (
                                        <div
                                            className="absolute bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs z-10 hidden md:block"
                                            style={{
                                                left: tooltip.x,
                                                top: tooltip.y,
                                                transform: 'translateX(-50%)'
                                            }}
                                        >
                                            <div className="font-medium">
                                                {tooltip.type === 'real' ? 'Perdita reale' : 'Obiettivo'}
                                            </div>
                                            <div className="text-gray-600">
                                                Settimana {tooltip.week}
                                            </div>
                                            <div>
                                                Valore: {tooltip.value} kg
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* X-axis labels */}
                                <div
                                    className={`flex mt-2 overflow-x-auto justify-between`}
                                    style={{
                                        paddingLeft: responsiveXLabelPadding,
                                        paddingRight: responsiveXLabelPadding
                                    }}
                                >
                                    {Array.from({ length: actualWeights.length }, (_, i) => i).map((weekIndex, index) => (
                                        <div
                                            key={`x-label-${index}`}
                                            className={`w-fit text-xs text-gray-500 whitespace-nowrap`}
                                        >
                                            {weekIndex + 1}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* X-axis title */}
                        <div className="venere-text-secondary text-end mt-2">
                            Settimana
                        </div>
                    </div>

                    <VenereProgressBoxLegend actualWeights={actualWeights} filteredActualWeights={filteredActualWeights} filteredGoalWeights={filteredGoalWeights} />

                    <div className="flex justify-center">
                        <VenereButton variant="secondary-royal" onClick={() => navigate('/venere/register-weight')}>
                            Registra i tuoi progressi
                        </VenereButton>
                    </div>
                </div>
            </VenereBox>
            <VenereBox variant="seasalt" className="max-w-[800px] flex flex-col gap-4">
                <div className="bg-white rounded-lg p-4 w-full">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[var(--venere-royal)]" />
                        <span className="venere-text-primary !text-[var(--venere-royal)]">Peso totale perso</span>
                    </div>
                    <div className="venere-text-primary">
                        -3 kg
                    </div>
                    <div className="w-full">
                        <div className="flex items-center justify-between">
                            <div className="venere-text-secondary">Peso attuale</div>
                            <div className="venere-text-purple">100kg</div>
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="venere-text-secondary">Media settimanale</div>
                            <div className="venere-text-purple">-0,6kg</div>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg p-4 w-full">
                <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[var(--venere-purple)]" />
                        <span className="venere-text-primary !text-[var(--venere-royal)]">Il tuo obiettivo</span>
                    </div>
                    <div className="venere-text-primary">
                        11% completato
                    </div>
                    <VenereProgressBar progress={11} variant="purple" />
                </div>
            </VenereBox>
        </div>
    )
}

export default VenereProgressBox

const VenereProgressBoxLegend: React.FC<{ actualWeights: number[], filteredActualWeights: number[], filteredGoalWeights: number[] }> = ({ actualWeights, filteredActualWeights, filteredGoalWeights }) => {
    return (
        <div className="bg-white rounded-lg p-4">
            <div className="venere-text-primary">
                Settimana {actualWeights.length}
            </div>
            <div className="w-full flex items-center justify-between">
                <div>
                    <div className="venere-text-royal">Peso: {filteredActualWeights[filteredActualWeights.length - 1]} kg</div>
                    <div className="venere-text-purple">Obiettivo: {filteredGoalWeights[filteredGoalWeights.length - 1]} kg</div>
                </div>
                <div className="flex flex-col items-start text-xs">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[var(--venere-royal)]" />
                        <span className="text-gray-600">Perdita reale</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[var(--venere-purple)]" />
                        <span className="text-gray-600">Obiettivo</span>
                    </div>
                </div>
            </div>
        </div>
    )
}
